import { ref, computed, readonly } from 'vue';
import { useRoute } from 'vue-router';
import usePageType from '@modules/discovery/composables/usePageType';
import useMeta from '@modules/discovery/composables/useMeta';
import { useNoOfProductsPerRow } from './useNoOfProductsPerRow';
import { prosBrowseZonesExperiment } from '../constants/prosBrowseZonesExperiment';
import { prosBrowseExperimentRowIndex } from '../constants/prosBrowseExperimentRowIndex';
import { groupProductsByRow } from '../utils/groupProductsByRow';
import getBrowseZoneCRowIndex from '../utils/getBrowseZoneCRowIndex';
import {
  ComputedRef, IProsCollectionConfig, ProsPanelConfig, ProsOptions, IProsConfig, IBanner, // eslint-disable-line no-unused-vars
} from '../types/sortable-grid.definitions';

let noOfProductsPerRowRef;
let route;
let propertiesRef;
let pageType;
let meta;
let isUSA;
let isWeddingRegistry;
const prosCollectionRef = ref({});

/**
 *
 * @param {string} key
 * @returns {number}
 */
function getRowIndex(key) {
  return +key.split('|')[0];
}

function getNextProsRowIndex(product, items) {
  const rows = groupProductsByRow(items, noOfProductsPerRowRef.value);
  const rowIndex = rows.findIndex((row) => row.includes(product)) + 1;
  return rowIndex;
}

/**
 *
 * @param {number} productId
 * @returns {boolean}
 */
function hasProsCollectionForProduct(productId) {
  return !!Object.values(prosCollectionRef.value).find((item) => item.prosCollection.recommendationConfig.productId === productId);
}

/**
 *
 * @param {number} rowIndex
 * @param {string} zone
 * @returns {string}
 */
function createKeyByZone(rowIndex, zone) {
  return `${rowIndex}${zone ? `|${ zone}` : ''}`;
}

/**
 *
 * @param {{recommendationConfig: Object, panelConfig: ProsPanelConfig, options: ProsOptions}} param0
 * @returns {IProsConfig}
 */
function createProsConfig({ recommendationConfig, panelConfig, options }) {
  return {
    prosCollection: {
      recommendationConfig: {
        categoryId: route.query.id,
        ...recommendationConfig,
      },
      panelConfig: {
        ...panelConfig,
        properties: {
          prosMEWBROWSEZoneBEnabled: true,
          prosDesktopBROWSEZoneBEnabled: true,
          ...(panelConfig?.properties || {}),
        },
      },
      options,
    },
    fullWidth: true,
    excludeRow: true,
  };
}

/**
 *
 * @param {IProsCollectionConfig} config
 * @param {number} rowIndex
 */
function addProsCollection(config, rowIndex) {
  const prosCollection = createProsConfig(config);
  const zone = config.recommendationConfig?.zone || '';
  const key = createKeyByZone(rowIndex, zone);

  prosCollectionRef.value = {
    ...prosCollectionRef.value,
    [key]: prosCollection,
  };
}

function clearProsCollection() {
  prosCollectionRef.value = [];
}

function clearMltProsCollection() {
  prosCollectionRef.value = Object.entries(prosCollectionRef.value).reduce((prosRef, [key, item]) => {
    /* istanbul ignore else */
    if (item.prosCollection.recommendationConfig.zone !== 'MLTPage_ZONE_A') {
      prosRef[key] = item;
    }
    return prosRef;
  }, {});
}

async function initMcomProsCollection() {
  const { carouselMiniStoreInjectionEnabled } = propertiesRef.value;

  // add pros collections directly to sortablegrid only for mcom & browse page
  /* istanbul ignore else */
  if (carouselMiniStoreInjectionEnabled && pageType.isBrowse) {
    const { Experimentation } = require('@vue-util/experimentation');

    const panelConfig = {
      panelConfig: {
        useWishList: false,
        properties: propertiesRef.value,
      },
    };

    Object.entries(prosBrowseExperimentRowIndex).forEach(async ([exp, index]) => {
      const isRecipeSelected = await Experimentation.isRecipeSelected(exp);
      /* istanbul ignore else */
      if (isRecipeSelected) {
        const zone = prosBrowseZonesExperiment[exp];
        const zoneExpConfig = {
          recommendationConfig: { zone },
          ...panelConfig,
          options: { hasBorder: true, extraPaddings: true },
        };
        addProsCollection(zoneExpConfig, index);
      }
    });

    // always add BROWSE_ZONE_C
    const browseZoneCRowIndex = getBrowseZoneCRowIndex(isUSA.value, isWeddingRegistry.value);
    const zoneCprosZonfig = {
      recommendationConfig: { zone: 'BROWSE_ZONE_C' },
      ...panelConfig,
      options: { hasBorder: true, extraPaddings: true },
    };
    addProsCollection(zoneCprosZonfig, browseZoneCRowIndex);
  }
}

/**
 *
 * @param {ComputedRef<IBanner[]>} productsRef
 * @returns {ComputedRef<Array<IBanner | IProsConfig>>}
 */
function populateProducts(productsRef) {
  const populatedProducts = computed(() => {
    let products = productsRef.value;
    const noOfProductsPerRow = noOfProductsPerRowRef.value;
    products = products.filter((item) => !item.prosCollection);
    /* istanbul ignore else */
    if (!products.length) {
      prosCollectionRef.value = [];
    }
    /* istanbul ignore else */
    if (prosCollectionRef.value) {
      const entries = Object.entries(prosCollectionRef.value);

      entries.forEach(([key, prosCollection]) => {
        /* istanbul ignore else */
        if (prosCollection) {
          const rowIndex = getRowIndex(key);
          const rows = groupProductsByRow(products, noOfProductsPerRow);
          const updatedRows = [
            ...rows.slice(0, rowIndex),
            [prosCollection],
            ...rows.slice(rowIndex),
          ];

          products = updatedRows.flatMap((row) => row);
        }
      });
    }

    return products;
  });

  return populatedProducts;
}

export function useProsCollection() {
  pageType = usePageType();
  meta = useMeta();
  propertiesRef = computed(() => meta?.value?.properties);
  route = useRoute();
  noOfProductsPerRowRef = useNoOfProductsPerRow();
  isUSA = computed(() => meta?.value?.context?.regionCode === 'US');
  isWeddingRegistry = computed(() => meta?.value?.context?.shoppingMode === 'WEDDING_REGISTRY');

  return {
    addProsCollection,
    initMcomProsCollection,
    populateProducts,
    getNextProsRowIndex,
    createProsConfig,
    clearProsCollection,
    hasProsCollectionForProduct,
    clearMltProsCollection,
    prosCollectionRef: readonly(prosCollectionRef),
  };
}

export default useProsCollection;
